import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Stack,
  CircularProgress,
  useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import {
  Visibility,
  VisibilityOff,
  Person,
  Lock,
  Storefront,
  ArrowBack,
  PointOfSale,
  Inventory2,
  BarChart,
} from "@mui/icons-material";
import { useAuth } from "@/hooks/useAuth";
import { useSettings } from "@/hooks/useSettings";
import type { LoginFormData } from "@/types";

const FEATURES = [
  {
    icon: PointOfSale,
    titleKey: "auth:login.feature_pos_title",
    descKey: "auth:login.feature_pos_desc",
    titleFallback: "Fast Checkout",
    descFallback: "Process sales instantly with barcode scanning and multiple payment methods.",
  },
  {
    icon: Inventory2,
    titleKey: "auth:login.feature_inventory_title",
    descKey: "auth:login.feature_inventory_desc",
    titleFallback: "Smart Inventory",
    descFallback: "Real-time stock tracking across all your outlets in one place.",
  },
  {
    icon: BarChart,
    titleKey: "auth:login.feature_analytics_title",
    descKey: "auth:login.feature_analytics_desc",
    titleFallback: "Powerful Analytics",
    descFallback: "Actionable insights and reports to grow your business faster.",
  },
];

const inputSx = (theme: Theme) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: '#f8fafc',
    transition: 'background-color 0.2s, box-shadow 0.2s',
    '& fieldset': {
      borderColor: 'rgba(0,0,0,0.1)',
      transition: 'border-color 0.2s',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main + '80',
    },
    '&.Mui-focused': {
      backgroundColor: '#fff',
      boxShadow: `0 0 0 3px ${theme.palette.primary.main}18`,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: '1.5px',
    },
    '& input': { fontWeight: 500, fontSize: '0.9375rem' },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: theme.palette.primary.main,
  },
});

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
const { login, loading, error, clearAuthError, isAuthenticated } = useAuth();
  const { settings } = useSettings();
  const storeCodeRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<LoginFormData>({
    username: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<Partial<LoginFormData>>({});
  const [shake, setShake] = useState(false);

  useEffect(() => {
    storeCodeRef.current?.focus();
  }, []);

  const validateForm = (): boolean => {
    const errors: Partial<LoginFormData> = {};

    if (!formData.storeCode?.trim()) {
      errors.storeCode = t("auth:validation.store_code_required") || "Store code is required";
    }
    if (!formData.username.trim()) {
      errors.username = t("auth:validation.username_required");
    } else if (formData.username.length < 3) {
      errors.username = t("auth:validation.username_min_length");
    }
    if (!formData.password) {
      errors.password = t("auth:validation.password_required");
    } else if (formData.password.length < 6) {
      errors.password = t("auth:validation.password_min_length");
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange =
    (field: keyof LoginFormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (error) clearAuthError();
      setFormData({ ...formData, [field]: event.target.value });
      if (formErrors[field]) {
        setFormErrors({ ...formErrors, [field]: undefined });
      }
    };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }

    const result = await login(formData);
    if (result.success) {
      navigate('/dashboard', { replace: true });
    } else {
      console.error("Login failed:", result.error);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") handleSubmit(event as any);
  };

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
      }}
    >
      {/* ── Left brand panel ── */}
      <Box
        sx={{
          width: { xs: "100%", md: "55%" },
          minHeight: { xs: 180, md: "100vh" },
          position: "relative",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: { xs: "center", md: "flex-start" },
          px: { xs: 3, md: 8 },
          py: { xs: 4, md: 6 },
          overflow: "hidden",
          background: `linear-gradient(135deg, #0d47a1 0%, #1976d2 40%, #42a5f5 70%, #7c3aed 100%)`,
        }}
      >
        {/* Decorative blobs */}
        <Box sx={{
          position: "absolute", top: "-10%", right: "-5%",
          width: "45%", height: "45%", borderRadius: "50%",
          background: "rgba(255,255,255,0.07)", filter: "blur(40px)",
        }} />
        <Box sx={{
          position: "absolute", bottom: "-8%", left: "-5%",
          width: "40%", height: "40%", borderRadius: "50%",
          background: "rgba(124,58,237,0.25)", filter: "blur(50px)",
        }} />
        <Box sx={{
          position: "absolute", top: "40%", right: "10%",
          width: "20%", height: "20%", borderRadius: "50%",
          background: "rgba(255,255,255,0.05)", filter: "blur(30px)",
        }} />

        {/* Logo + wordmark */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: { xs: 0, md: 6 },
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box
            component="img"
            src="/assets/logo.svg"
            alt="Ankassa"
            sx={{ width: { xs: 36, md: 48 }, height: { xs: 36, md: 48 }, objectFit: "contain" }}
          />
          <Typography
            variant="h5"
            fontWeight={700}
            color="#fff"
            sx={{ letterSpacing: "-0.3px", display: { xs: "block", md: "block" } }}
          >
            {settings?.storeName || "Ankassa"}
          </Typography>
        </Box>

        {/* Tagline — desktop only */}
        <Box sx={{ display: { xs: "none", md: "block" }, position: "relative", zIndex: 1 }}>
          <Typography
            variant="h3"
            fontWeight={700}
            color="#fff"
            sx={{ lineHeight: 1.2, mb: 2, maxWidth: 460 }}
          >
            {t("auth:login.brand_headline") || "Run your store.\nSmarter."}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "rgba(255,255,255,0.75)", mb: 5, maxWidth: 400, lineHeight: 1.7 }}
          >
            {t("auth:login.brand_subheadline") ||
              "Everything you need to manage sales, inventory, and growth — all in one place."}
          </Typography>

          {/* Feature highlights */}
          <Stack spacing={2.5}>
            {FEATURES.map(({ icon: Icon, titleFallback, descFallback, titleKey, descKey }) => (
              <Box key={titleFallback} sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Box
                  sx={{
                    width: 40, height: 40, borderRadius: 2, flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: "rgba(255,255,255,0.15)",
                    backdropFilter: "blur(8px)",
                  }}
                >
                  <Icon sx={{ fontSize: 20, color: "#fff" }} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="#fff" fontWeight={600}>
                    {t(titleKey) || titleFallback}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.65)", lineHeight: 1.5 }}>
                    {t(descKey) || descFallback}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>

      {/* ── Right form panel ── */}
      <Box
        sx={{
          width: { xs: "100%", md: "45%" },
          minHeight: { xs: "calc(100vh - 180px)", md: "100vh" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "#fff",
          px: { xs: 3, sm: 5, md: 6 },
          py: { xs: 4, md: 6 },
          position: "relative",
        }}
      >
        {/* Back to home */}
        <Box sx={{ position: "absolute", top: 24, left: { xs: 16, md: 24 } }}>
          <Button
            component={Link}
            to="/"
            startIcon={<ArrowBack fontSize="small" />}
            size="small"
            sx={{
              color: "text.secondary",
              textTransform: "none",
              fontWeight: 500,
              '&:hover': { color: "primary.main", bgcolor: "transparent" },
            }}
          >
            {t("auth:login.back_to_home") || "Back to home"}
          </Button>
        </Box>

        <Box
          sx={{
            width: "100%",
            maxWidth: 400,
            '@keyframes shake': {
              '0%, 100%': { transform: 'translateX(0)' },
              '20%, 60%': { transform: 'translateX(-6px)' },
              '40%, 80%': { transform: 'translateX(6px)' },
            },
            animation: shake ? 'shake 0.45s ease-in-out' : 'none',
          }}
        >
          {/* Heading */}
          <Box mb={4}>
            <Typography variant="h4" fontWeight={700} color="text.primary" gutterBottom sx={{ letterSpacing: "-0.5px" }}>
              {t("auth:login.welcome_back") || "Welcome back"}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t("auth:login.subtitle_form") || "Sign in to your account to continue"}
            </Typography>
          </Box>

          {/* Error alert */}
          {error && (
            <Alert
              severity="error"
              onClose={clearAuthError}
              sx={{ mb: 3, borderRadius: 2 }}
            >
              {error}
            </Alert>
          )}

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              <TextField
                label={t("auth:login.store_code") || "Store Code"}
                value={formData.storeCode || ""}
                onChange={handleInputChange("storeCode")}
                onKeyPress={handleKeyPress}
                error={!!formErrors.storeCode}
                helperText={formErrors.storeCode}
                inputRef={storeCodeRef}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Storefront fontSize="small" sx={{ color: "text.disabled" }} />
                    </InputAdornment>
                  ),
                }}
                sx={inputSx(theme)}
              />

              <TextField
                label={t("auth:login.username") || "Username"}
                value={formData.username}
                onChange={handleInputChange("username")}
                onKeyPress={handleKeyPress}
                error={!!formErrors.username}
                helperText={formErrors.username}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person fontSize="small" sx={{ color: "text.disabled" }} />
                    </InputAdornment>
                  ),
                }}
                sx={inputSx(theme)}
              />

              <TextField
                label={t("auth:login.password") || "Password"}
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleInputChange("password")}
                onKeyPress={handleKeyPress}
                error={!!formErrors.password}
                helperText={formErrors.password}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock fontSize="small" sx={{ color: "text.disabled" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                        {showPassword
                          ? <VisibilityOff fontSize="small" />
                          : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={inputSx(theme)}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  mt: 0.5,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #0d47a1 100%)`,
                  boxShadow: `0 4px 16px ${theme.palette.primary.main}44`,
                  "&:hover": {
                    background: `linear-gradient(135deg, #1565c0 0%, ${theme.palette.primary.main} 100%)`,
                    boxShadow: `0 6px 20px ${theme.palette.primary.main}55`,
                    transform: "translateY(-1px)",
                  },
                  "&:disabled": { background: theme.palette.action.disabledBackground },
                  transition: "all 0.25s ease",
                }}
              >
                {loading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={18} color="inherit" />
                    {t("auth:login.signing_in") || "Signing in…"}
                  </Box>
                ) : (
                  t("auth:login.signin_button") || "Sign In"
                )}
              </Button>
            </Stack>
          </Box>
        </Box>

        {/* Footer */}
        <Typography
          variant="caption"
          color="text.disabled"
          align="center"
          sx={{ position: "absolute", bottom: 20 }}
        >
          © {new Date().getFullYear()} Ankassa Digital
        </Typography>
      </Box>
    </Box>
  );
};

export default LoginPage;
